import React, { useState, useMemo } from 'react';
import { safeFormatDate } from '../../../shared';
import {
  Box,
  Typography,
  Button,
  Divider,
  Tab,
  Tabs,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  extractJsonPaths,
  resolveJsonPath,
  previewValue,
  type JsonPathEntry,
} from '../../../shared/utils/jsonPath';
import {
  Add as AddIcon,
  Search as InvestigateIcon,
  TableChart as DatasetIcon,
} from '@mui/icons-material';
import DetailSidebar from '../../../shared/components/DetailSidebar';
import DetailRow from '../../../shared/components/DetailRow';
import EventTraceTimeline from './EventTraceTimeline';
import CategoryIcon from './CategoryIcon';
import { friendlyType, extractSeverity, buildSummary, toEventEnvelope } from '../utils/eventDisplay';
import JsonCodeEditor from '../../../shared/components/JsonCodeEditor';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import type { LiveEvent } from '../../../types/frontend';

interface EventDetailPanelProps {
  customerId: string;
  event: LiveEvent;
  onClose: () => void;
  onCreateRule: (event: LiveEvent) => void;
  /** Given only while the event_datasets switch is on and the caller may create datasets. */
  onCreateDataset?: (event: LiveEvent) => void;
  onStartInvestigation?: (event: LiveEvent) => void;
}

const EventDetailPanel: React.FC<EventDetailPanelProps> = ({
  customerId,
  event,
  onClose,
  onCreateRule,
  onCreateDataset,
  onStartInvestigation,
}) => {
  const [tab, setTab] = useState<'details' | 'trace' | 'raw'>('details');
  const severity = extractSeverity(event);
  const summary = buildSummary(event);
  const rawEnvelope = toEventEnvelope(event);

  // Raw-view path filter: pick/type a JSONPath to narrow the displayed event to
  // that sub-tree. Completion + previews come from the same shared jsonPath utils
  // the rule editor's field mappings use.
  const [rawFilter, setRawFilter] = useState('');
  const rawPaths = useMemo(() => extractJsonPaths(rawEnvelope), [rawEnvelope]);
  const filteredEvent = useMemo<Record<string, unknown>>(() => {
    const q = rawFilter.trim();
    if (!q) return rawEnvelope;
    const resolved = resolveJsonPath(q, rawEnvelope);
    if (resolved === undefined) return {};
    if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
      return resolved as Record<string, unknown>;
    }
    return { [q]: resolved };
  }, [rawFilter, rawEnvelope]);

  const toolbar = (
    <>
      {/* A narrow pane wraps the buttons onto a second line rather than squeeze their labels past their edges. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, py: 1, '& > .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' } }}>
        {onStartInvestigation && (
          <Button size="small" variant="contained" color="primary" startIcon={<InvestigateIcon />} onClick={() => onStartInvestigation(event)}>
            Investigate
          </Button>
        )}
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onCreateRule(event)}>
          Create Rule
        </Button>
        {onCreateDataset && (
          <Button size="small" variant="outlined" startIcon={<DatasetIcon />} onClick={() => onCreateDataset(event)}>
            Create Dataset
          </Button>
        )}
        {tab === 'raw' && (
          <Autocomplete<JsonPathEntry, false, false, true>
            freeSolo
            size="small"
            options={rawPaths}
            value={null}
            inputValue={rawFilter}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.path)}
            onInputChange={(_, v, reason) => {
              if (reason === 'input' || reason === 'clear') setRawFilter(v);
            }}
            onChange={(_, selected) => {
              if (selected && typeof selected !== 'string') setRawFilter(selected.path);
            }}
            filterOptions={(opts, state) => {
              const q = state.inputValue.trim().toLowerCase();
              const matched = q
                ? opts.filter(
                    (o) =>
                      o.path.toLowerCase().includes(q) ||
                      previewValue(o.value).toLowerCase().includes(q),
                  )
                : opts;
              return matched.slice(0, 50);
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{option.path}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', maxWidth: 160 }} noWrap>
                  {previewValue(option.value, 40)}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter to path…" />
            )}
            sx={{ flex: 1, minWidth: 200 }}
          />
        )}
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 2, minHeight: 36 }}>
        <Tab label="Details" value="details" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
        <Tab label="Trace" value="trace" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
        <Tab label="Raw" value="raw" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
      </Tabs>
      <Divider />
    </>
  );

  return (
    <DetailSidebar title="Event Details" onClose={onClose} toolbar={toolbar}>
      <Box sx={{ p: 2 }}>
        {tab === 'details' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CategoryIcon source={event.source} size={18} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{friendlyType(event)}</Typography>
            </Box>
            {summary && summary !== '—' && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{summary}</Typography>
            )}

            <DetailRow label="Severity" value={severity?.label} />
            <DetailRow label="Time" value={safeFormatDate(event.timestamp)} />
            <DetailRow label="Source" value={event.source} />
            <DetailRow label="Detail type" value={event.detail_type} />
            <DetailRow label="Account" value={event.account} />
            <DetailRow label="Region" value={event.region} />
            <DetailRow label="Resources" value={event.resources && event.resources.length ? event.resources.join(', ') : undefined} />
          </>
        )}

        {tab === 'trace' && (
          <EventTraceTimeline customerId={customerId} event={event} />
        )}

        {tab === 'raw' && (
          <JsonCodeEditor
            value={filteredEvent}
            onChange={() => {}}
            readOnly
            showExpand={false}
            minHeight={420}
            actions={<CopyIconButton text={JSON.stringify(filteredEvent, null, 2)} />}
          />
        )}
      </Box>
    </DetailSidebar>
  );
};

export default EventDetailPanel;
